import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Stack } from '@nearst/ui';
import { ArrowRightIcon, ClockIcon, ExclamationTriangleIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid';

import { CARD_STATES } from '../../hooks/useCardEmptyState';

import styles from './EmptyState.module.scss';

const ICON_FOR_STATE = {
	[CARD_STATES.SETUP]: ArrowRightIcon,
	[CARD_STATES.PENDING]: ClockIcon,
	[CARD_STATES.NO_TRACTION]: MagnifyingGlassIcon,
	[CARD_STATES.ERROR]: ExclamationTriangleIcon
};

const HEADLINE_FOR_STATE = {
	[CARD_STATES.SETUP]: 'Get started',
	[CARD_STATES.PENDING]: 'Data is on its way',
	[CARD_STATES.NO_TRACTION]: 'No data for this period',
	[CARD_STATES.ERROR]: "We couldn't load this data"
};

/**
 * Renders the appropriate empty-state UI for a card whose data isn't ready.
 * Consumes the output of `useCardEmptyState`.
 *
 * Returns `null` when `state === 'data'` — parent should branch on that and
 * render its real content instead.
 *
 * @param {object} props
 * @param {string} props.state — one of CARD_STATES (setup/pending/no-traction/data)
 * @param {string|null} props.copy — body copy from useCardEmptyState
 * @param {string|null} props.ctaLabel — CTA button label, only used for setup state
 * @param {string|null} props.ctaHref — CTA button href, only used for setup state
 */
export const EmptyState = ({ state, copy, ctaLabel, ctaHref }) => {
	if (state === CARD_STATES.DATA || !state) return null;

	const Icon = ICON_FOR_STATE[state];
	const headline = HEADLINE_FOR_STATE[state];
	const showCta = state === CARD_STATES.SETUP && ctaLabel && ctaHref;

	return (
		<div className={styles.root} data-testid={`empty-state-${state}`} role="status">
			<Stack space="0.75rem">
				<div className={styles.headline}>
					{Icon && <Icon className={styles.icon} aria-hidden="true" />}
					<span>{headline}</span>
				</div>
				{copy && <p className={styles.copy}>{copy}</p>}
				{showCta && (
					<div>
						<Button as={Link} to={ctaHref} primary>
							{ctaLabel}
						</Button>
					</div>
				)}
			</Stack>
		</div>
	);
};
